<template>
  <div>
    <v-app-bar 
      app
      clipped-right 
      height="56"
      :class="{ 'hidden-on-fullscreen': isFullscreenDialogOpen }"
      :style="shouldShowBanner ? 'top: 40px' : ''"
      class="toolbar-main"
    >

      <!-- Recherche client + infos client réunies, à gauche comme chez Strapp -->
      <div class="toolbar-customer-search toolbar-customer-search--toolbar">
        <div class="toolbar-customer-inner">
          <div class="toolbar-customer-container">
            <SelectCustomer
              ref="customerSearch"
              :clearable="true"
              @clear="clearCustomer"
              @keydown.native="handleShortcut"
              @open-spotlight="openSpotlight"
              @toggle-drawer="switchNavigationDrawerVisibility"
              :selected-customer="selectedCustomer"
              class="select-customer-toolbar"
              :show-clear-btn="!!selectedCustomer.clientId"
              :latest-database="latestDatabase"
              :download-loading="downloadLoading"
              :download-latest-database="downloadLatestDatabase"
            />
          </div>

        </div>
      </div>

      <!-- Outils externes puis utilisateur, à droite comme chez Strapp -->
      <div class="toolbar-right">
        <ToolsMenu />
        <ThemeToggle />
        <UserMenu />
      </div>
    </v-app-bar>

    <QrCodeModal :show.sync="showQrModal" :customer="selectedCustomer" />
    
  <!-- Barre d'information client fusionnée dans la toolbar -->
  </div>
</template>
<script>
  import moment from "moment";
  import { mapState } from "vuex";
  import DatabaseService from "../services/DatabaseService";
  import SelectCustomer from "./SelectCustomer";
  import QrCodeModal from "./QrCodeModal.vue";
  import ToolsMenu from "./ToolsMenu.vue";
  import ThemeToggle from "./ThemeToggle.vue";
  import UserMenu from "./UserMenu.vue";
  export default {
    data() {
      return {
        context: "hublot",
        downloadLoading: false,
        latestDatabaseCache: null,
        showQrModal: false,
      };
    },
    components: {
      ToolsMenu,
      ThemeToggle,
      UserMenu,
      SelectCustomer,
      QrCodeModal,
    },
    methods: {
      switchNavigationDrawerVisibility() {
        this.$store.dispatch("setNavigationDrawerVisibility", true);
      },
      clearCustomer() {
        this.$store.dispatch("setSelectedCustomer", {});
      },
      showQrCode() {
        this.showQrModal = true;
      },
      handleShortcut(e) {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
          e.preventDefault();
        this.$refs.customerSearch?.$el?.querySelector("input")?.focus();
        }
      },
      openSpotlight() {
        this.$store.dispatch("openSpotlightSearch");
      },
      impersonateSelectedCustomer() {
        if (!this.selectedCustomer?.clientId) return;
        this.$store
          .dispatch("impersonateCustomer", this.selectedCustomer)
          .catch((error) => {
            console.error("Erreur lors de l'impersonation:", error);
            this.$store.dispatch("addNotification", {
              text: "Impossible d'impersonate ce magasin",
              color: "error",
            });
          });
      },
      copyToClipboard(element, value = "Valeur") {
        navigator.clipboard
          .writeText(element)
          .then(() => {
            this.$store.dispatch("addNotification", {
              text: `${value} copié dans votre presse-papier`,
              color: "success",
            });
          })
          .catch((e) => {
            console.error(e);
            this.$store.dispatch("addNotification", {
              text: `Impossible de copier ${value} dans votre presse-papier`,
              color: "error",
            });
          });
      },
      getCategoryIcon(type) {
        const icons = {
          Client: "person",
          Prospect: "person_outline",
          Partenaire: "business",
          Fournisseur: "local_shipping",
        };
        return icons[type] || "folder";
      },
      getEnseigneName(idEnseigne) {
        const enseigneMap = {
          5: "Carrefour",
          1: "Leclerc",
          3: "Intermarché",
          2: "Super U",
          4: "Indépendant",
        };
        return enseigneMap[idEnseigne] || "Enseigne inconnue";
      },
      formatDatabaseTimeWithDate(dateString) {
        const dbDate = moment(dateString);
        const today = moment().startOf("day");
        if (dbDate.isSame(today, "day")) {
          return `de ${dbDate.format("HH[h]mm")}`;
        } else {
          return `du ${dbDate.format("DD/MM/YYYY à HH[h]mm")}`;
        }
      },
      fetchLatestDatabase(clientId) {
        // Appel API pour récupérer toutes les databases du client
        DatabaseService.getLatest(clientId)
          .then((databases) => {
            if (databases && databases.length > 0) {
              // Trier par date décroissante et prendre la première
              const latest = databases.sort(
                (a, b) => new Date(b.date) - new Date(a.date),
              )[0];
              this.latestDatabaseCache = {
                clientId: clientId,
                database: latest,
              };
            } else {
              this.latestDatabaseCache = {
                clientId: clientId,
                database: null,
              };
            }
          })
          .catch((error) => {
            console.error(
              "Erreur lors de la récupération de la dernière base de données:",
              error,
            );
            this.latestDatabaseCache = {
              clientId: clientId,
              database: null,
            };
          });
      },
      downloadLatestDatabase() {
        if (!this.latestDatabase) {
          this.$store.dispatch("addNotification", {
            text: "Aucune base de données disponible pour ce client",
            color: "warning",
          });
          return;
        }
        this.downloadLoading = true;
        const formattedDate = moment(this.latestDatabase.date).format(
          "DD-MM-YYYY_HH-mm",
        );
        DatabaseService.download(
          this.latestDatabase.uuid,
          this.selectedCustomer.name,
          formattedDate,
          this.latestDatabase.aux,
        )
          .then(() => {
            this.$store.dispatch("addNotification", {
              text: "Téléchargement de la base de données démarré",
              color: "success",
            });
          })
          .catch((error) => {
            console.error("Erreur lors du téléchargement:", error);
            this.$store.dispatch("addNotification", {
              text: "Erreur lors du téléchargement de la base de données",
              color: "error",
            });
          })
          .finally(() => {
            this.downloadLoading = false;
          });
      },
      // ...
    },
    computed: {
      ...mapState({
        navigationDrawerVisibility: (state) =>
          state.home.navigationDrawerVisibility,
        customers: (state) => state.customers.list,
        selectedCustomer: (state) => state.customers.selected,
        allDatabases: (state) => state.home.allDatabases,
      }),
      isFullscreenDialogOpen() {
        const databasesDialog = this.$store.state.databases?.dialog;
        return databasesDialog || false;
      },
      shouldShowBanner() {
        const adminId = this.$store.getters.hublotAdminId
        const clientId = this.$store.getters.currentClientIdInToken
        return adminId && clientId && String(adminId) !== String(clientId)
      },
      latestDatabase() {
        if (!this.selectedCustomer?.clientId) return null;
        if (
				this.latestDatabaseCache?.clientId === this.selectedCustomer.clientId
        ) {
          return this.latestDatabaseCache.database;
        }
        return null;
      },
      selectedCustomerLabel() {
        const customerName = this.selectedCustomer?.name || 'Client sélectionné'
        const customerId = this.selectedCustomer?.clientId
        return customerId ? `${customerName} (#${customerId})` : customerName
      }
    },
    watch: {
      "selectedCustomer.clientId": {
        handler(newClientId) {
          if (newClientId) {
            this.fetchLatestDatabase(newClientId);
          } else {
            this.latestDatabaseCache = null;
          }
        },
        immediate: true,
      },
    },
  };
</script>
<style lang="scss">
// Barre du haut : couleur, filet et absence d'ombre viennent de design-system.scss (.v-app-bar.toolbar-main).
// Ici seulement la mise en page ; l'apparence du sélecteur client est dans SelectCustomer.vue.
.toolbar-main {
  padding: 0 var(--space-4) !important;
}

// Largeur souple, plafonnée à 640px (recherche de 480px plus les actions du magasin) : elle cède la place aux icônes
.toolbar-customer-search {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  max-width: 640px;
  min-width: 0;
  height: 56px;
}

.toolbar-customer-inner {
  display: flex;
  align-items: center;
  width: 100%;
  gap: var(--space-2);
  min-width: 0;
}

.toolbar-customer-container {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-left: auto;
  flex: none;
}

// Masquer la toolbar quand un dialog plein écran est ouvert
.v-app-bar.hidden-on-fullscreen {
  display: none !important;
}

@media (max-width: 960px) {
  .toolbar-customer-search {
    max-width: 100%;
  }
}

@media (max-width: 599.98px) {
  .toolbar-main {
    padding: 0 var(--space-2) !important;
  }
  .toolbar-right { gap: 0; }

  .toolbar-customer-search {
    max-width: 100%;
  }
}
</style>
